// Tata letak dokumen = daftar BLOK berurut (kop, judul, data, tanda tangan, S&K …).
//
// Kenapa blok, bukan koordinat bebas: isi dokumen berubah-ubah (jumlah item, riwayat sesi,
// blok garansi/refund yang kadang ada kadang tidak). Koordinat mati membuat isi panjang saling
// tindih atau menyisakan lubang; blok yang mengalir selalu rapi berapa pun isinya.
//
// Yang DISIMPAN hanya PERUBAHAN Apo terhadap bawaan (urutan, sembunyi, lebar, perataan) —
// bukan salinan seluruh susunan. Dengan begitu blok baru yang ditambahkan kemudian tetap
// muncul sendiri, dan perbaikan bawaan tak tertutup oleh simpanan lama.

/** 'third' = sepertiga. Ada karena kertas ½ A4 cuma menyisakan 129 mm tinggi: tiga kartu
 *  pendek (garansi · survei · ikuti-kami) HARUS berbagi satu baris, kalau ditumpuk notanya
 *  jadi dua lembar. */
export type Lebar = 'full' | 'half' | 'third';
export type Rata = 'left' | 'center' | 'right';

export interface SecDef {
  id: string;
  label: string;          // nama yang dibaca Apo di editor
  /** Teks bawaan blok ini bila isinya kalimat tetap (judul, pernyataan) — bisa Apo ubah sendiri. */
  teks?: string;
  /** Keterangan isi bila blok mengambil dari data, bukan teks tetap (mis. kop dari Profil Toko). */
  isiDari?: string;
  width?: Lebar;
  align?: Rata;
  /** true = blok ini tak bisa disembunyikan (mis. nomor servis pada tanda terima). */
  wajib?: boolean;
}

export interface TataSimpan {
  /** Ukuran kertas KHUSUS dokumen ini. Kosong = ikut setelan umum (Pengaturan → 🖨️ Cetak).
   *  Surat Kuasa memuat pernyataan hukum + 9 baris identitas + 3 tanda tangan — itu dokumen
   *  A4, memaksanya ke ½ A4 hanya membuat 2 lembar atau teks terlalu rapat untuk dibaca. */
  kertas?: 'A5' | 'A4';
  urut?: string[];
  sembunyi?: string[];
  lebar?: Record<string, Lebar>;
  rata?: Record<string, Rata>;
  /** Penyetel halus per blok — jarak atas/bawah (mm) & ukuran huruf (px). Kosong = bawaan. */
  gaya?: Record<string, GayaBlok>;
  /** Teks blok yang diubah Apo (hanya yang beda dari bawaan). */
  teks?: Record<string, string>;
  /** Bantalan kiri-kanan khusus dokumen ini (mm). Kosong = ikut setelan umum. */
  marginMm?: number;
  /** Blok TEKS tambahan buatan Apo (catatan, promo, alamat cabang kedua, dsb). */
  tambahan?: BlokTambahan[];
}

export interface BlokTambahan { id: string; label: string; teks: string }

/** Awalan id blok buatan Apo — dipakai membedakannya dari blok bawaan sistem. */
export const PREFIKS_TAMBAHAN = 'x_';

/**
 * Daftar blok yang berlaku = blok bawaan dokumen + blok teks tambahan buatan Apo.
 * Blok tambahan diletakkan di akhir bila belum pernah diurutkan; `urutkanTata` yang
 * kemudian menempatkannya sesuai susunan tersimpan.
 */
export function semuaBlok(bawaan: SecDef[], simpan: TataSimpan | null | undefined): SecDef[] {
  const tmb = (simpan?.tambahan ?? []).map((t) => ({ id: t.id, label: t.label || 'Blok teks', teks: t.teks }));
  return [...bawaan, ...tmb];
}

/** Isi blok teks tambahan (kosong bila bukan blok tambahan). */
export const isiTambahan = (id: string, simpan: TataSimpan | null | undefined): string =>
  simpan?.tambahan?.find((t) => t.id === id)?.teks ?? '';

/** Teks blok yang berlaku: ubahan Apo bila ada, selebihnya bawaan. */
export const teksBlok = (def: SecDef, simpan: TataSimpan | null | undefined): string =>
  simpan?.teks?.[def.id] ?? def.teks ?? '';

export interface GayaBlok { mt?: number; mb?: number; fs?: number }

/** CSS untuk penyetel halus satu blok. String kosong = tak ada yang perlu diubah. */
export function gayaBlokCss(g: GayaBlok | undefined): string {
  // Nilai DIJEPIT ke rentang masuk akal: atribut min/max pada kotak isian tak menahan apa pun
  // saat nilainya dibaca, jadi "400" atau "-40" akan merusak dokumen. Sekaligus menutup
  // kemungkinan nilai bukan-angka menyelinap ke atribut style.
  const jepit = (v: unknown, lo: number, hi: number): number | null => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : null;
  };
  if (!g) return '';
  const b: string[] = [];
  const mt = jepit(g.mt, 0, 40), mb = jepit(g.mb, 0, 40), fs = jepit(g.fs, 6, 30);
  if (g.mt != null && mt != null) b.push(`margin-top:${mt}mm`);
  if (g.mb != null && mb != null) b.push(`margin-bottom:${mb}mm`);
  if (g.fs != null && fs != null) b.push(`font-size:${fs}px`);
  return b.join(';');
}

/**
 * Urutan blok saja (TERMASUK yang disembunyikan) — dipakai EDITOR, supaya Apo tetap melihat
 * blok yang ia sembunyikan dan bisa menampilkannya lagi.
 */
export function urutkanTata(bawaan: SecDef[], simpan: TataSimpan | null | undefined): SecDef[] {
  const urut = (simpan?.urut ?? []).filter((id) => bawaan.some((b) => b.id === id));
  if (!urut.length) return [...bawaan];
  const hasil: SecDef[] = urut.map((id) => bawaan.find((b) => b.id === id)!);
  // Blok bawaan yang belum dikenal simpanan disisipkan dekat tetangga bawaannya — bukan dibuang,
  // bukan dilempar ke ujung. Tanpa ini, blok BARU tak pernah terlihat oleh toko.
  bawaan.forEach((b, i) => {
    if (hasil.some((h) => h.id === b.id)) return;
    const sebelum = bawaan.slice(0, i).reverse().find((x) => hasil.some((h) => h.id === x.id));
    const at = sebelum ? hasil.findIndex((h) => h.id === sebelum.id) + 1 : 0;
    hasil.splice(at, 0, b);
  });
  return hasil;
}

/** Susunan akhir untuk DICETAK: urutan + sembunyi + lebar/perataan pilihan Apo. */
export function terapkanTata(bawaan: SecDef[], simpan: TataSimpan | null | undefined): SecDef[] {
  const t = simpan ?? {};
  const sembunyi = new Set(t.sembunyi ?? []);

  const hasil = urutkanTata(bawaan, t);

  return hasil
    .filter((s) => !sembunyi.has(s.id) || s.wajib)
    .map((s) => ({                                   // salinan: objek bawaan dipakai ulang tiap cetak
      ...s,
      ...(t.lebar?.[s.id] ? { width: t.lebar[s.id] } : {}),
      ...(t.rata?.[s.id] ? { align: t.rata[s.id] } : {}),
    }));
}
